import {Component, effect, input, Input, OnDestroy, OnInit, QueryList, untracked, ViewChildren} from '@angular/core';

import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {DateService} from '@services/date.service';

import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';

import {MetaData} from '@models/adminstration/meta-data';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {MetadataElasticSearchService} from '@services/elastic-search/metadata/metadata-elastic-search.service';
import {ELASTIC_SEARCH_TYPE} from '@constants/elastic-search/elastic-search-type.enum';
import {Machine} from '@models/machine/machine';
import {PaginateControlSettings} from '@models/paginate-control-settings';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {catchError, startWith, takeUntil} from 'rxjs/operators';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {ModalService} from '@services/modal/modal.service';
import {GatewayService} from '@services/administration/gateway/gateway.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {ELASTIC_SEARCH_SORT_ORDER} from '@models/elastic-search/request/elastic-search-sort-order.enum';
import {MetadataEditComponent} from '@components/administration/metadata/modals/metadata-edit.component';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';

@Component({
  selector: 'scitis-admin-metadata',
  templateUrl: './metadata.component.html',
  styleUrls: ['./metadata.component.scss'],
  standalone: false
})
export class MetadataComponent implements OnInit, OnDestroy {

  readonly parent = input<Gateway | Machine | null>(null);
  readonly type = input<ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA | ELASTIC_SEARCH_TYPE.MACHINE_META_DATA | null>(null);

  @Input()
  isAdminRole: boolean = false;

  @Input()
  isTechAdminRole: boolean = false;

  @Input()
  isSuperAdminRole: boolean = false;

  public addMetadataButtonDisabled = false;
  public metaDataCount: number = 0;
  public searchTerm: string = '';
  public metaDataArray: MetaData[] = [];
  public readonly itemsPerPageOptions: number[] = [10, 20, 50, 100, 250];
  public paginateSettings: PaginateControlSettings = new PaginateControlSettings({
    id: 'metaDataPagination',
    itemsPerPage: this.itemsPerPageOptions[0]
  });

  private sortValue: ElasticSearchSortValue = new ElasticSearchSortValue('key', ELASTIC_SEARCH_SORT_ORDER.ASC, false);
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private initialized = false;
  private loadedInputContext = '';
  private readonly PageName: string = 'metadata.component';

  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();

  constructor(
      private modalService: ModalService,
      private loadingIndicator: LoadingIndicatorService,
      private translationService: TranslateService,
      private notificationService: NotificationService,
      private arrayOperationService: ArrayOperationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private elasticSearchService: MetadataElasticSearchService,
      private dateService: DateService,
      private gatewayService: GatewayService,
      private machineService: MachineService
  ) {
    this.resetProperties();
    effect(() => {
      const parent = this.parent();
      const type = this.type();
      if (this.initialized) {
        untracked(() => this.loadMetadataForInputContext(parent, type));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.initialized = true;
    this.loadMetadataForInputContext(this.parent(), this.type());
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.paginateSettings = {
      ...this.paginateSettings,
      itemsPerPage: itemsPerPage,
      currentPage: 1,
    };
    this.resetProperties();
    this.setMetaData();
  }

  public onLoadPageNumber(pageNumber: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      currentPage: pageNumber,
    };
    this.setMetaData();
  }

  public onSearch(): void {
    this.setMetaData();
  }

  protected onAddMetaData() {
    const modalRef = this.modalService.open(MetadataEditComponent, {size: 'md'});

    modalRef.componentInstance.metaData = new MetaData();
    modalRef.componentInstance.parentId = this.parent()?.id;
    modalRef.componentInstance.parentName = this.parent()?.name;
    modalRef.componentInstance.type = this.type();
    modalRef.componentInstance.isNewMetaData = true;

    modalRef.result.then((result: MetaData) => {
      this.loadingIndicator.show();

      const parent = this.parent();
      const type = this.type();
      if (!parent?.id || !type) {
        return;
      }

      let createObservable: Observable<any> = new Observable<any>();
      if (type === ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA) {
        createObservable = this.gatewayService.saveMetaData(String(parent.id), result);
      } else if (type === ELASTIC_SEARCH_TYPE.MACHINE_META_DATA) {
        createObservable = this.machineService.saveMetaData(String(parent.id), result);
      }

      createObservable.subscribe({
        next: (success: MetaData) => {
          this.metaDataCount++;
          this.paginateSettings = {
            ...this.paginateSettings,
            totalItems: this.metaDataCount,
          };
          this.metaDataArray.push(success);
          const message = this.translationService.instant('MetaData %key added.').replaceAll('%key', result.key?.toBold());

          this.loadingIndicator.hide();
          this.notificationService.success(message, this.PageName, false);
        }, error: (error) => {
          this.loadingIndicator.hide();
          const message = this.translationService.instant('Error adding metaData %key.').replaceAll('%key', result.key?.toBold());
          this.notificationService.error(message, error, this.PageName, true);
        }
      });
    });
  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.sortValue = this.getSortValue({column, direction});
    this.resetProperties();
    this.setMetaData();
  }

  private getSortValue({column, direction}: SortEvent): ElasticSearchSortValue {
    let sortOrder: ELASTIC_SEARCH_SORT_ORDER;
    if (direction === ELASTIC_SEARCH_SORT_ORDER.ASC.toLowerCase()) {
      sortOrder = ELASTIC_SEARCH_SORT_ORDER.ASC;
    } else if (direction === ELASTIC_SEARCH_SORT_ORDER.DESC.toLowerCase()) {
      sortOrder = ELASTIC_SEARCH_SORT_ORDER.DESC;
    } else {
      sortOrder = ELASTIC_SEARCH_SORT_ORDER.NONE;
    }
    if (column !== 'updateTime')
      return new ElasticSearchSortValue(column, sortOrder);

    return new ElasticSearchSortValue(column, sortOrder, true);
  }

  get userIsAllowedToEdit(): boolean {
    return this.isTechAdminRole || this.isSuperAdminRole;
  }

  get machineMetadataAvailable(): boolean {
    return (this.metaDataArray && this.metaDataArray.length > 0);
  }

  formatTime(updateTime: Date | undefined | null): string {
    if (!updateTime) {
      return '---';
    }

    const mediumDate = this.dateService.getMediumDate(updateTime);
    if (mediumDate) {
      return mediumDate;
    }
    return '---';
  }

  private loadMetadataForInputContext(
      parent: Gateway | Machine | null,
      type: ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA | ELASTIC_SEARCH_TYPE.MACHINE_META_DATA | null
  ): void {
    if (!parent?.id || !type) {
      this.loadedInputContext = '';
      return;
    }
    const inputContext = `${type}:${parent.id}`;
    if (inputContext === this.loadedInputContext) {
      return;
    }
    this.loadedInputContext = inputContext;
    this.loadingIndicator.show(this.translationService.instant('Loading metadata ...'));
    this.setMetaData();
  }

  private setMetaData(): void {
    const parent = this.parent();
    const type = this.type();
    if (!parent?.id || !type) {
      return;
    }

    if (this.elasticSearchService.isInvalidSearchTerm(this.searchTerm)) {
      this.metaDataArray = [];
      this.metaDataCount = 0;
      this.paginateSettings = {...this.paginateSettings, totalItems: this.metaDataCount};
      return;
    }

    this.metaDataArray = [];
    this.metaDataCount = 0;
    this.loadingIndicator.show(this.translationService.instant('Loading'));
    this.addMetadataButtonDisabled = true;
    const countObserver = this.searchTerm ? of(null) : this.setMetaDataCount();
    const listObserver = this.elasticSearchService
    .listMetaData(this.searchTerm, this.sortValue, this.paginateSettings.currentPage, this.paginateSettings.itemsPerPage, parent.id, type)
    .pipe(
        startWith(null),
        catchError(error => {
          const message = this.translationService.instant('Error getting metadata list.');
          this.loadingIndicator.hide();
          this.notificationService.error(message, error, this.PageName, true);
          return of(null);
        })
    );

    combineLatest([listObserver, countObserver])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([list, count]: [
          ElasticSearchResponseEntity | null,
          ElasticSearchResponseEntity | null
    ]) => {
      if (list && !this.metaDataArray.length) {
        this.metaDataArray = list.result?.data || [];

        if (this.searchTerm) {
          this.metaDataCount = list.result.totalHits;
          this.paginateSettings = {
            ...this.paginateSettings,
            totalItems: this.metaDataCount
          };
        }

        this.loadingIndicator.hide();
      }

      if (count && !this.metaDataCount) {
        this.metaDataCount = count.result.totalHits;
        this.paginateSettings = {
          ...this.paginateSettings,
          totalItems: this.metaDataCount
        };
      }
    });
  }

  private setMetaDataCount(): Observable<any> {
    const parent = this.parent();
    const type = this.type();
    if (!parent?.id || !type) {
      console.error(`Cannot get metadata count. Invalid parent id '${parent?.id}'or type '${type}'.`);
      return of(null);
    }

    return this.elasticSearchService.countMetaData(parent.id, type)
    .pipe(startWith(null), catchError(err => {
      console.error(`Error getting metadata count.`, JSON.stringify(err));
      return of(null);
    }));
  }

  private resetProperties() {
    this.metaDataArray = new Array<MetaData>();
    this.paginateSettings = {
      ...this.paginateSettings,
      currentPage: 1
    };
  }

  protected onDeleteMetadataSuccess(metaDataItem: MetaData) {
    this.metaDataCount--;
    this.paginateSettings = {
      ...this.paginateSettings,
      totalItems: this.metaDataCount,
    };
    this.arrayOperationService.delete(metaDataItem, this.metaDataArray, []);

  }

  protected onMetadataSaveSuccess(metaDataItem: MetaData) {
    this.arrayOperationService.update(metaDataItem, this.metaDataArray, []);
  }
}
